import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';

import { getConfig } from '../../config/config-helper';
import { useAppDispatch } from '../../hooks';
import { fetchSolution, type ISolution } from '../../reducers/solutionsSlice';
import ProjectIcon from '../common/ProjectIcon';
import { PageCenteredLayout } from '../layout/PageCenteredLayout';
import CopyUrlButton from '../controls/CopyUrlButton';
import { useSolution } from '../../hooks/useSolution';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { classNames } from '../../utilities/styleUtils';
import PublishUnPublishButton from '../controls/PublishUnPublishButton';
import { useToken } from '../../hooks/useToken';
import SectionLayout from '../layout/SectionLayout';
import Grid3Layout from '../layout/Grid3Layout';
import Tag from '../common/Tag';
import MarkdownContent from '../common/MarkdownContent';
import { StarSolutionButton } from '../controls/StarSolutionButton';
import Button from '../controls/Button';
import { DeploymentTotalTag } from '../project/DeploymentTotalTag';
import { HyperlinkOpenButton } from '../controls/HyperlinkOpenButton';
import { UserIcon } from '@heroicons/react/16/solid';
import TierTag from '../project/TierTag';
import DataOpsInfinity from '../../assets/images/icons/DataOps_darkblue_icon.svg';
import { useNavigationTab } from '../../hooks/useNavigationTab';
import { repoSizeWarning } from '../../utilities/constants';
import WarningWithTooltip from '../common/WarningWithTooltip';
import { ChevronDownIcon, RocketLaunchIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { fetchSolutionDeployments, selectSolutionDeployments } from '../../reducers/solutionDeploymentsSlice';
import { fetchSnowflakeAccounts, selectAccounts } from '../../reducers/snowflakeAccountsSlice';
import { useSelector } from 'react-redux';
import SolutionDemoItem from '../project/SolutionDemoItem';
import SetupModal from '../modals/setup/SetupModal';
import { checkIfSolutionIsConfiguredForKeyPairAuth } from '../../utilities/repositoryUtils';

function whichReleaseStatus(solution: ISolution | undefined): 'Published' | 'Draft' {
  if (solution?.fullPath.includes('/solution-drafts')) {
    return 'Draft';
  }
  return 'Published';
}

export default function SolutionPage() {
  const { dataopsliveBaseUrl } = getConfig();

  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const token = useToken();
  const { canPublish } = useCurrentUser();
  const { solution } = useSolution(id);
  const fullPath = `/solutions/${id ?? ''}`;
  const solutionDolUrl = `${dataopsliveBaseUrl}/${solution?.fullPath ?? ''}`;
  const avatarUrl = solution?.avatarUrl ?? solution?.listing?.icon;
  const [releaseStatus, setReleaseStatus] = useState<'Published' | 'Draft'>(whichReleaseStatus(solution));
  const [triggerPublish, setTriggerPublish] = useState<boolean>(false);

  // Solution deployments (new demos)
  const solutionDeployments = useSelector(selectSolutionDeployments);
  const snowflakeAccounts = useSelector(selectAccounts);
  // Run once to get the solution details
  useEffect(() => {
    dispatch(fetchSolution({ token, fullPath, iid: id })).catch((err) => {
      console.log('Fetch solution error:', err);
    });
  }, []);

  useEffect(() => {
    if (searchParams.has('publish')) {
      if (releaseStatus === 'Draft' && canPublish) {
        setTriggerPublish(true);
      }
      searchParams.delete('publish');
      setSearchParams(searchParams);
    }
  }, [searchParams]);

  useNavigationTab(
    solution?.listing?.name
      ? {
          section: 'discover/solutions',
          label: `${solution?.listing?.name}`,
          href: fullPath,
          status: solution?.listing?.name ? 'loaded' : 'loading',
        }
      : null,
    [solution?.listing?.name, token, releaseStatus],
  );

  // Fetch solution deployments and snowflake accounts
  useEffect(() => {
    if (token) {
      dispatch(fetchSolutionDeployments(token)).catch((err) => {
        console.error('Failed to fetch solution deployments:', err);
      });
      dispatch(fetchSnowflakeAccounts(token)).catch((err) => {
        console.error('Failed to fetch snowflake accounts:', err);
      });
    }
  }, [token, dispatch]);

  useEffect(() => {
    if (solution?.fullPath.includes('/solution-drafts')) {
      setReleaseStatus('Draft');
    } else {
      setReleaseStatus('Published');
    }
  }, [solution?.fullPath]);

  // Filter solution deployments for this specific solution
  const filteredSolutionDeployments = useMemo(() => {
    return solutionDeployments.filter((deployment) => {
      return deployment.project_id.toString() === id;
    });
  }, [solutionDeployments, id]);

  // Helper function to get account name
  const getAccountName = (accountId: number) => {
    const account = snowflakeAccounts.find((acc) => acc.id === accountId);
    return account?.account ?? `Account ID: ${accountId}`;
  };

  if (solution?.listing?.name === undefined || solution?.listing?.name === '') {
    setTimeout(() => {
      window.location.reload();
    }, 3000);
    return <SolutionPageSkeleton />;
  }

  let repoSizeWarningTag = <></>;

  if (solution.repositorySize > 50000000) {
    // Repository Size greater than 50MB
    repoSizeWarningTag = (
      <Tag color={'lightGray'} className="z-10">
        <WarningWithTooltip tooltipMessage={repoSizeWarning} />
      </Tag>
    );
  }

  return (
    <>
      <main>
        <header className="relative isolate pt-10">
          <div aria-hidden="true" className="absolute inset-0 -z-10 overflow-hidden">
            <div className="absolute left-16 top-full -mt-16 transform-gpu opacity-50 blur-3xl xl:left-1/2 xl:-ml-80">
              <div
                style={{
                  clipPath:
                    'polygon(100% 38.5%, 82.6% 100%, 60.2% 37.7%, 52.4% 32.1%, 47.5% 41.8%, 45.2% 65.6%, 27.5% 23.4%, 0.1% 35.3%, 17.9% 0%, 27.7% 23.4%, 76.2% 2.5%, 74.2% 56%, 100% 38.5%)',
                }}
                className={classNames(
                  'aspect-[1154/678] w-[72.125rem] bg-gradient-to-br',
                  releaseStatus === 'Published'
                    ? 'from-dataops-primary-light-blue to-dataops-secondary-dark-blue'
                    : 'from-dataops-secondary-orange to-dataops-secondary-red',
                )}
              />
            </div>
            <div className="absolute inset-x-0 bottom-0 h-px bg-gray-900/5" />
          </div>

          <PageCenteredLayout>
            <div className="mx-auto flex items-center justify-between gap-x-8 lg:mx-0 lg:max-w-none">
              <div className="flex items-center gap-x-6">
                <ProjectIcon iconSrc={avatarUrl} />
                <h1>
                  <div className="mt-1 text-base font-semibold text-dataops-primary-dark-blue">
                    {releaseStatus === 'Draft' ? '[Draft] ' : ''}
                    {solution?.listing?.name}
                  </div>
                  <a
                    href={solutionDolUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-1 text-sm/6 text-gray-500 hover:underline"
                  >
                    <img src={DataOpsInfinity} className="w-4 h-4 mt-0.5" />
                    <span className="truncate text-ellipsis w-3/4">{solution.fullPath}</span>
                  </a>
                </h1>
              </div>
              <div className="flex flex-col items-end gap-y-2 sm:gap-x-1">
                {/* Controls */}
                <div className="flex gap-x-1">
                  <StarSolutionButton solution={solution} />
                  <CopyUrlButton view="solution" />
                </div>
                <div className="flex gap-x-1">
                  {repoSizeWarningTag}

                  <TierTag tier={solution?.listing?.tier} />
                  <Tag color={'lightGray'}>
                    <UserIcon className="w-3 h-3 mr-1" />
                    <span>{solution?.listing?.creator}</span>
                  </Tag>
                  <DeploymentTotalTag solution={solution} />
                </div>
              </div>
            </div>
          </PageCenteredLayout>
        </header>

        <PageCenteredLayout>
          <Grid3Layout>
            <SectionLayout label="Actions" className="bg-dataops-supporting-gray/40 md:col-span-1 md:col-start-3">
              <div className="space-y-1">
                <Actions solution={solution} triggerPublish={triggerPublish} />
              </div>
            </SectionLayout>
            <SectionLayout label="Deployments" className="md:col-span-1 md:col-start-3 md:row-start-2">
              {filteredSolutionDeployments.length === 0 ? (
                <div className="text-sm/6 text-gray-500">No deployments yet</div>
              ) : (
                <div className="flex flex-col h-full">
                  <div className="overflow-y-auto max-h-64 flex-1">
                    <ul role="list" className="w-full space-y-1 p-1">
                      {filteredSolutionDeployments.map((deployment) => (
                        <li key={deployment.id}>
                          <SolutionDemoItem
                            deployment={deployment}
                            accountName={getAccountName(deployment.snowflake_account_id)}
                            mini={true}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="pt-3 mt-3 border-t border-gray-200">
                    <Link to="/deployments" className="text-sm text-sky-600 hover:underline">
                      See legacy deployments
                    </Link>
                  </div>
                </div>
              )}
            </SectionLayout>
            <SectionLayout label="Description" className="md:col-span-2 md:row-start-1">
              <p className="text-sm/6">{solution?.listing?.description}</p>
            </SectionLayout>
            <SectionLayout label="Tags" className="md:col-span-2 md:row-start-2">
              <div className="grid md:grid-cols-2 gap-x-2 gap-y-4 text-sm/6 text-gray-500">
                <TagsList label="Features" tags={solution?.listing?.snowflake_feature ?? []} />
                <TagsList label="Verticals" tags={solution?.listing?.vertical_applicability ?? []} />
                <TagsList label="Use Cases" tags={solution?.listing?.use_case ?? []} />
                <TagsList label="Workload" tags={solution?.listing?.workload ?? []} />
                <TagsList label="Partners" tags={solution?.listing?.partner ?? []} />
              </div>
            </SectionLayout>
            {solution?.listing?.preview && (
              <SectionLayout label="Learn more" className="md:col-span-3">
                <MarkdownContent markdown={solution?.listing?.preview} />
              </SectionLayout>
            )}
            {solution?.listing?.help && (
              <SectionLayout label="Help" className="md:col-span-3">
                <MarkdownContent markdown={solution?.listing?.help} />
              </SectionLayout>
            )}
          </Grid3Layout>
        </PageCenteredLayout>
      </main>
    </>
  );
}

function TagsList({ label, tags }: { label: string; tags: string[] }) {
  return tags.length !== 0 ? (
    <div className="">
      <h3>{label}</h3>
      <div className="space-x-1">
        {tags.map((tag) => (
          <Tag key={tag}>{tag}</Tag>
        ))}
      </div>
    </div>
  ) : (
    <></>
  );
}

function Actions({ solution, triggerPublish }: { solution: ISolution; triggerPublish: boolean }) {
  const { canPublish, canConsume } = useCurrentUser();

  if (canPublish) {
    return <PublisherActions solution={solution} triggerPublish={triggerPublish} />;
  }
  if (canConsume) {
    return <ConsumerActions solution={solution} />;
  }
  return <ReadOnlyActions />;
}

function PublisherActions({ solution, triggerPublish }: { solution: ISolution; triggerPublish: boolean }) {
  const { canPublish } = useCurrentUser();
  const releaseStatus = whichReleaseStatus(solution);
  return (
    <>
      <ConsumerActions solution={solution} />
      {canPublish && (
        <PublishUnPublishButton
          solution={solution}
          canPublish={canPublish}
          releaseStatus={releaseStatus}
          triggerPublish={triggerPublish}
          size="large"
        />
      )}
    </>
  );
}

type DeployMode = 'default' | 'legacy';

function ConsumerActions({ solution }: { solution: ISolution }) {
  const navigate = useNavigate();
  const token = useToken();
  const { dataopsliveBaseUrl } = getConfig();
  const tier = solution?.listing?.tier ?? 2;
  const [isKeyPairCompatible, setIsKeyPairCompatible] = useState<boolean | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [legacyModalOpen, setLegacyModalOpen] = useState(false);
  // Default to null until we know compatibility; resolved below
  const [deployMode, setDeployMode] = useState<DeployMode | null>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (token && solution?.id) {
      let cancelled = false;
      setIsKeyPairCompatible(null);
      setDeployMode(null);
      checkIfSolutionIsConfiguredForKeyPairAuth(`${dataopsliveBaseUrl}/`, token, Number(solution.id))
        .then((result) => {
          if (!cancelled) {
            setIsKeyPairCompatible(result.isConfigured);
            // Default to legacy if not compatible, otherwise new-style
            setDeployMode(result.isConfigured ? 'default' : 'legacy');
          }
        })
        .catch(() => {
          if (!cancelled) {
            setIsKeyPairCompatible(false);
            setDeployMode('legacy');
          }
        });
      return () => {
        cancelled = true;
      };
    }
  }, [token, solution?.id, dataopsliveBaseUrl]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (tier === 4) {
    return <HyperlinkOpenButton solution={solution} />;
  }

  const notKeyPairCompatible = isKeyPairCompatible === false;
  const keyPairWarningMessage =
    'This solution is not yet configured for key pair authentication. Use the legacy deployment option instead.';

  const legacyDeprecationWarningMessage =
    'Legacy deployments are deprecated and may not be supported in the future. Specifically, deployments using a LEGACY_SERVICE user will not be possible from May 2026.';

  const handleMainButtonClick = () => {
    if (deployMode === 'legacy') {
      setLegacyModalOpen(true);
    } else {
      navigate(`/new/deployment?solution=${solution.id}`);
    }
  };

  return (
    <div className="flex flex-col">
      <SetupModal
        open={legacyModalOpen}
        setOpen={setLegacyModalOpen}
        cancelButtonRef={cancelButtonRef}
        prefillProjectTitle={solution?.listing?.name ?? ''}
        templateId={Number(solution.id)}
        preInstanceSetupCheckList={solution?.listing?.pre_instance_setup_checklist}
        preInstanceSetupMessage={solution?.listing?.pre_instance_setup_message}
        requiredDataShares={solution?.listing?.required_data_shares}
        verticalThemes={solution?.listing?.vertical_themes}
        customerNameSuffix={solution?.listing?.customer_name_suffix}
        repositorySize={solution?.repositorySize ?? 0}
      />
      <div className="relative inline-flex" ref={dropdownRef}>
        <Button
          intent="primary"
          size="large"
          className="rounded-r-none border-r border-dataops-primary-light-blue/30 w-full"
          onClick={handleMainButtonClick}
        >
          {notKeyPairCompatible && deployMode === 'default' && (
            <WarningWithTooltip tooltipMessage={keyPairWarningMessage} iconStyle="w-4 h-4 mr-1.5 text-yellow-300" />
          )}
          <RocketLaunchIcon className="w-4 h-4 mr-1" />
          {deployMode === 'legacy' ? 'Deploy (Legacy mode)' : 'Deploy'}
        </Button>
        <Button
          intent="primary"
          size="large"
          className="rounded-l-none px-2"
          onClick={() => {
            setDropdownOpen((prev) => !prev);
          }}
          aria-label="More deployment options"
        >
          <ChevronDownIcon className="w-4 h-4" />
        </Button>
        {dropdownOpen && (
          <div className="absolute right-0 top-full mt-1 w-72 rounded-md bg-white shadow-lg ring-1 ring-black/5 z-20">
            <div className="py-1 divide-y divide-gray-100">
              <button
                className={classNames(
                  'flex w-full items-start gap-x-3 px-4 py-3 text-left',
                  notKeyPairCompatible
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-50 cursor-pointer',
                )}
                disabled={notKeyPairCompatible}
                onClick={() => {
                  if (!notKeyPairCompatible) {
                    setDeployMode('default');
                    setDropdownOpen(false);
                  }
                }}
              >
                <Cog6ToothIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div
                    className={classNames(
                      'text-sm flex items-center gap-x-1',
                      deployMode === 'default' ? 'font-semibold' : 'font-medium',
                    )}
                  >
                    Default mode
                    {notKeyPairCompatible && (
                      <WarningWithTooltip
                        tooltipMessage={keyPairWarningMessage}
                        iconStyle="w-3.5 h-3.5 text-yellow-400"
                      />
                    )}
                  </div>
                  <p className="text-xs mt-0.5 text-gray-400">
                    Default mode with enhanced security, better error handling, and more features.
                  </p>
                </div>
              </button>
              <button
                className="flex w-full items-start gap-x-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-50 cursor-pointer"
                onClick={() => {
                  setDeployMode('legacy');
                  setDropdownOpen(false);
                }}
              >
                <Cog6ToothIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div
                    className={classNames(
                      'text-sm flex items-center gap-x-1',
                      deployMode === 'legacy' ? 'font-semibold' : 'font-medium',
                    )}
                  >
                    Legacy mode
                    <WarningWithTooltip
                      tooltipMessage={legacyDeprecationWarningMessage}
                      iconStyle="w-3.5 h-3.5 text-yellow-400"
                    />
                  </div>
                  <p className="text-xs mt-0.5 text-gray-400">
                    Legacy mode that creates a new service user for every deployment, and forks the solution project for
                    each deployment.
                  </p>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReadOnlyActions() {
  return <div className="text-sm/6 text-gray-500">No actions for a read-only user.</div>;
}

function SolutionPageSkeleton() {
  return (
    <main>
      <header className="relative isolate pt-10">
        <div aria-hidden="true" className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute left-16 top-full -mt-16 transform-gpu opacity-50 blur-3xl xl:left-1/2 xl:-ml-80">
            <div
              style={{
                clipPath:
                  'polygon(100% 38.5%, 82.6% 100%, 60.2% 37.7%, 52.4% 32.1%, 47.5% 41.8%, 45.2% 65.6%, 27.5% 23.4%, 0.1% 35.3%, 17.9% 0%, 27.7% 23.4%, 76.2% 2.5%, 74.2% 56%, 100% 38.5%)',
              }}
              className={classNames(
                'aspect-[1154/678] w-[72.125rem] bg-gradient-to-br',
                'from-dataops-supporting-gray to-dataops-supporting-black',
              )}
            />
          </div>
          <div className="absolute inset-x-0 bottom-0 h-px bg-gray-900/5" />
        </div>

        <PageCenteredLayout>
          <div className="mx-auto flex max-w-2xl items-center justify-between gap-x-8 lg:mx-0 lg:max-w-none">
            <div className="flex items-center gap-x-6">
              <ProjectIcon iconSrc={undefined} className="animate-pulse" />
              <h1>
                <div className="mt-1 w-60 h-6 rounded-lg text-base font-semibold bg-dataops-supporting-gray bg-shine"></div>
                <a
                  href={'solutionDolUrl'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 flex items-center space-x-1 text-sm/6 text-gray-500 hover:underline"
                >
                  <img src={DataOpsInfinity} className="w-4 h-4 mt-0.5 animate-pulse opacity-40" />
                  <div className="w-60 h-5 rounded-lg bg-dataops-supporting-gray bg-shine"></div>
                </a>
              </h1>
            </div>
            <div className="flex items-center sm:gap-x-1">
              {/* Controls */}
              <div className="w-20 h-5 rounded-sm bg-dataops-supporting-gray bg-shine"></div>
              <div className="w-14 h-5 rounded-sm bg-dataops-supporting-gray bg-shine"></div>
              <div className="w-20 h-6 rounded-md bg-dataops-supporting-gray bg-shine"></div>
            </div>
          </div>
        </PageCenteredLayout>
      </header>

      <PageCenteredLayout>
        <Grid3Layout>
          <SectionLayout
            skeleton
            label="Actions"
            className="bg-dataops-supporting-gray/40 md:col-span-1 md:col-start-3"
          >
            <div className="space-y-1">{/* <Actions /> */}</div>
          </SectionLayout>
          <SectionLayout skeleton label="Deployments" className="md:col-span-1 md:col-start-3 md:row-start-2">
            <></>
          </SectionLayout>
          <SectionLayout skeleton label="Description" className="md:col-span-2 md:row-start-1">
            <div className="space-y-2">
              <p className="w-full h-4 rounded-lg bg-shine"></p>
              <p className="w-full h-4 rounded-lg bg-shine"></p>
              <p className="w-1/2 h-4 rounded-lg bg-shine"></p>
            </div>
          </SectionLayout>
          <SectionLayout skeleton label="Tags" className="md:col-span-2 md:row-start-2">
            <div className="grid md:grid-cols-2 gap-x-2 gap-y-4 text-sm/6 text-gray-500">
              <div className="flex space-x-1">
                <div className="w-20 h-5 rounded-sm bg-dataops-supporting-gray bg-shine"></div>
                <div className="w-14 h-5 rounded-sm bg-dataops-supporting-gray bg-shine"></div>
                <div className="w-10 h-5 rounded-sm bg-dataops-supporting-gray bg-shine"></div>
                <div className="w-14 h-5 rounded-sm bg-dataops-supporting-gray bg-shine"></div>
              </div>
              <div className="flex space-x-1">
                <div className="w-20 h-5 rounded-sm bg-dataops-supporting-gray bg-shine"></div>
                <div className="w-10 h-5 rounded-sm bg-dataops-supporting-gray bg-shine"></div>
                <div className="w-20 h-5 rounded-sm bg-dataops-supporting-gray bg-shine"></div>
                <div className="w-14 h-5 rounded-sm bg-dataops-supporting-gray bg-shine"></div>
              </div>
              <div className="flex space-x-1">
                <div className="w-20 h-5 rounded-sm bg-dataops-supporting-gray bg-shine"></div>
                <div className="w-20 h-5 rounded-sm bg-dataops-supporting-gray bg-shine"></div>
                <div className="w-14 h-5 rounded-sm bg-dataops-supporting-gray bg-shine"></div>
              </div>
            </div>
          </SectionLayout>
        </Grid3Layout>
      </PageCenteredLayout>
    </main>
  );
}
